import { action, makeObservable, observable } from 'mobx';
import {
  userInfoStore,
  roundInfoStore,
  gameConfigStore,
  notificationStore,
  betStore,
} from '@pascal/pascal-core/shared/core/store';
import { applicationStore } from '@pascal/pascal-core';
import { freeBetStore } from '@pascal/pascal-core/shared/core/store/freeBetStore';

import { marketsStore } from './marketsStore';
import { hiLoBetTypes } from '../enums/betTypes';
import { createGuid } from '../helpers/createGuid';
import { checkDoBetModel } from '../helpers/supporters';
import { gameLiveConnection } from '../services/GameConnection';

class DoBetStore {
  winAmount = 0;

  isShowWinPopUp = false;

  isLoadingBetButton = false;

  isLoadingReBetButton = false;

  totalBetsAmount = 0;

  betData = {
    betId: null,
    amount: gameConfigStore?.limits?.defaultBetAmount || gameConfigStore?.limits?.minBet || 0,
    freeBetId: null,
    isBetAccepted: false,
    winAmount: null,
    isDisabled: false,
  };

  reBetData = [];

  constructor() {
    makeObservable(this, {
      betData: observable.deep,
      reBetData: observable.deep,
      winAmount: observable,
      isLoadingBetButton: observable,
      isLoadingReBetButton: observable,
      isShowWinPopUp: observable,
      totalBetsAmount: observable,
      doBetSender: action.bound,
      handleDoBetResult: action.bound,
      updatedReBetData: action.bound,
      setReBetData: action.bound,
      setIsLoadingBetButton: action.bound,
      setIsLoadingReBetButton: action.bound,
      updateIsDisabledBetSection: action.bound,
      updateBetAccepted: action.bound,
      updateTotalBetsAmount: action.bound,
      updateIsShowWinPopUp: action.bound,
      setBetAmount: action.bound,
      setWinAmount: action.bound,
      detectAndSetReBetData: action.bound,
      resetGameStates: action.bound,
    });
  }

  updateIsShowWinPopUp(isShowWinPopUp) {
    this.isShowWinPopUp = isShowWinPopUp;
  }

  updateBetAccepted(isBetAccepted, betId) {
    this.betData = {
      ...this.betData,
      isBetAccepted,
      betId,
    };
  }

  updatedReBetData(betData) {
    this.reBetData = [...this.reBetData, betData];
  }

  setReBetData(bets) {
    this.reBetData = bets;
  }

  setIsLoadingBetButton(isLoadingBetButton) {
    this.isLoadingBetButton = isLoadingBetButton;
  }

  setIsLoadingReBetButton(isLoadingReBetButton) {
    this.isLoadingReBetButton = isLoadingReBetButton;
  }

  resetGameStates() {
    this.betData = {
      betId: null,
      amount: gameConfigStore?.limits?.defaultBetAmount || gameConfigStore?.limits?.minBet || 0,
      freeBetId: null,
      isBetAccepted: false,
      winAmount: null,
      isDisabled: false,
    };
    this.setWinAmount(0);
    this.updateTotalBetsAmount(0, true);
    notificationStore.setNotificationType(null);
  }

  setBetAmount(amount) {
    this.betData = { ...this.betData, amount };
  }

  updateIsDisabledBetSection(isDisabledBetSection) {
    this.betData = {
      ...this.betData,
      isDisabled: isDisabledBetSection,
    };
  }

  doBetSender() {
    const { limits } = gameConfigStore;
    const { balance: userBalance } = userInfoStore;
    const { amount } = this.betData;
    const { roundId } = roundInfoStore;
    const { gameTypeId } = gameConfigStore;
    const { isDemo } = applicationStore;
    const {
      selectedMarket: { marketType, odd, number },
    } = marketsStore;
    const { activeFreeBet } = freeBetStore;

    const responseCode = checkDoBetModel(limits, userBalance, amount, isDemo);
    this.setIsLoadingBetButton(true);
    if (responseCode) {
      notificationStore.setNotificationType({
        responseCode,
      });

      this.setIsLoadingBetButton(false);
      this.updateIsDisabledBetSection(false);
    } else {
      const doBetModel = {
        number,
        odd,
        amount,
        roundId,
        gameTypeId,
        freeBetId: activeFreeBet?.id && !isDemo ? activeFreeBet?.id : null,
        betData: [
          {
            amount,
            marketType,
            id: createGuid(),
          },
        ],
      };

      gameLiveConnection.doBet(doBetModel);
    }
  }

  updateTotalBetsAmount(amount, isReset = false) {
    if (isReset) {
      this.totalBetsAmount = 0;
    } else {
      this.totalBetsAmount += Number(amount);
    }
  }

  setWinAmount(winAmount) {
    this.winAmount = winAmount;
  }

  detectAndSetReBetData(data, isFromFreeBetToggle) {
    if (data.length) {
      const { activeFreeBet } = freeBetStore;
      const bets = data.filter((bet) => {
        if (!activeFreeBet?.id && bet.freeBetId) {
          return false;
        }

        if (activeFreeBet?.id) {
          if (!bet.freeBetId) {
            return false;
          }

          return bet.freeBetId === activeFreeBet.id;
        }

        return true;
      });

      if (isFromFreeBetToggle) {
        this.setReBetData(bets);
      } else {
        const betsForParse = bets.map((bet) => {
          bet.additionalParams.markets = bet.additionalParams.markets.filter(
            (market) => !hiLoBetTypes[market.marketType]
          );

          if (bet.additionalParams.markets.length && this.reBetData.length) {
            this.setReBetData([]);
          }

          return bet;
        });

        betsForParse.forEach((betForParse) => {
          if (betForParse && betForParse.additionalParams.markets.length) {
            betForParse.additionalParams.markets.forEach((item) => {
              this.updatedReBetData({
                ...item,
                userId: betForParse.userId,
                freeBetId: betForParse.freeBetId,
                currencyCode: betForParse.currencyCode,
                playerExternalId: betForParse.playerExternalId,
              });
            });
          }
        });
      }

      // betForParse.additionalParams.markets = bet.additionalParams.markets.filter(
      //   (market) => !hiLoBetTypes[market.marketType]
      // );

      // if (
      //   this.reBetData.length &&
      //   ((!betData.betData[0].isReBet &&
      //     this.reBetData.some((item) => item.roundId !== betData.roundId)) ||
      //     (hiLoBetTypes[betData.betData[0].marketType] && !findHiOrLoResult))
      // ) {
      //   this.updatedReBetData();
      // }
    }
  }

  handleDoBetResult(doBetData) {
    const { responseCode } = doBetData;

    const isReBet = (doBetData.betData.markets || doBetData.betData).some((item) => item.isReBet);
    if (!isReBet && this.reBetData.length) {
      const { liveBets } = betStore;
      const hasBets = liveBets.some((item) => item.userId === userInfoStore.userId);

      if (!hasBets) {
        this.setReBetData([]);
      }
    }

    this.setIsLoadingBetButton(false);
    this.setIsLoadingReBetButton(false);
    notificationStore.setNotificationType({ responseCode });
  }
}

export const doBetStore = new DoBetStore();
